/**
 * Pure error-parsing helpers for {@link AuthErrorService}.
 *
 * GAP-FE-MONO-01: extracted verbatim from `auth-error.service.ts` to shrink the
 * monolithic service and make the security-sensitive classification + redaction
 * logic independently unit-testable.
 *
 * Every function here is PURE: output depends only on its arguments (including
 * the explicitly-passed `timestamp: Date` and `supportUrl`). There are no reads
 * of instance state, no Angular DI, and no side effects. This is what makes the
 * functions safe to call from templates and trivial to test with hand-computed
 * fixtures.
 *
 * SECURITY: {@link sanitizeErrorForLogging} is the redaction boundary. Its body
 * is byte-identical to the original service implementation — passwords, tokens,
 * secrets, and sensitive URL params are stripped before anything reaches a log
 * sink. Do not weaken the patterns or the field-skip logic.
 */
import { HttpErrorResponse } from '@angular/common/http';

import { sanitizeUrl } from '../utils/url-sanitizer';
import {
  AuthError,
  AuthErrorCode,
  ApiErrorResponse,
  AUTH_ERROR_MESSAGES,
  AUTH_ERROR_RECOVERY_GUIDANCE,
  AUTH_ERROR_CATEGORIES,
  AUTH_ERROR_RECOVERY_ACTIONS,
  AUTH_ERROR_SEVERITIES
} from '../models/auth-errors.model';

/**
 * Parse HTTP error response into a structured AuthError.
 *
 * @param error The HttpErrorResponse to classify
 * @param timestamp The time the error was observed (passed in for purity)
 * @param supportUrl Optional support link copied into the result
 */
export function parseHttpError(
  error: HttpErrorResponse,
  timestamp: Date,
  supportUrl?: string
): AuthError {
  const apiError = extractApiError(error);
  const code = detectErrorCode(error, apiError);

  return {
    code,
    message: AUTH_ERROR_MESSAGES[code],
    technicalMessage: apiError.message || error.message,
    statusCode: error.status,
    severity: AUTH_ERROR_SEVERITIES[code],
    category: AUTH_ERROR_CATEGORIES[code],
    recoveryAction: AUTH_ERROR_RECOVERY_ACTIONS[code],
    recoveryGuidance: AUTH_ERROR_RECOVERY_GUIDANCE[code],
    retryAfterSeconds: apiError.retryAfter,
    unlockTime: apiError.unlockAt ? new Date(apiError.unlockAt) : undefined,
    supportLink: supportUrl,
    originalError: error,
    requestId: apiError.requestId || extractRequestId(error),
    timestamp,
    shouldLog: true,
    showNotification: shouldShowNotification(code)
  };
}

/**
 * Extract API error details from HTTP response body.
 */
export function extractApiError(error: HttpErrorResponse): ApiErrorResponse {
  const body = error.error;

  if (!body) {
    return {};
  }

  if (typeof body === 'string') {
    return { message: body };
  }

  return {
    error: body.error,
    message: body.message || body.error,
    code: body.code,
    statusCode: body.statusCode || error.status,
    details: body.details,
    retryAfter: body.retryAfter || body.retry_after,
    unlockAt: body.unlockAt || body.unlock_at,
    requestId: body.requestId || body.request_id
  };
}

/**
 * Detect the error code from HTTP response and API error.
 */
export function detectErrorCode(error: HttpErrorResponse, apiError: ApiErrorResponse): AuthErrorCode {
  // Check for explicit error code from API
  if (apiError.code) {
    const mappedCode = mapApiCodeToAuthErrorCode(apiError.code);
    if (mappedCode !== AuthErrorCode.UNKNOWN) {
      return mappedCode;
    }
  }

  // Check HTTP status code
  switch (error.status) {
    case 0:
      return AuthErrorCode.NETWORK_ERROR;
    case 401:
      return detectUnauthorizedErrorCode(apiError);
    case 403:
      return AuthErrorCode.PERMISSION_DENIED;
    case 404:
      // Only classify as ACCOUNT_NOT_FOUND if this is an auth-related endpoint
      // Other 404s (e.g., /api/v1/tickers/EUR_USD) are resource-not-found, not auth errors
      if (isAuthRelatedEndpoint(error.url)) {
        return AuthErrorCode.ACCOUNT_NOT_FOUND;
      }
      // For non-auth endpoints, use UNKNOWN to avoid misleading auth error messages
      return AuthErrorCode.UNKNOWN;
    case 408:
      return AuthErrorCode.TIMEOUT;
    case 422:
      return AuthErrorCode.VALIDATION_ERROR;
    case 429:
      return AuthErrorCode.RATE_LIMIT_EXCEEDED;
    case 500:
    case 502:
    case 503:
      return AuthErrorCode.SERVER_ERROR;
    case 504:
      return AuthErrorCode.TIMEOUT;
    default:
      return detectErrorCodeFromMessage(apiError.message || apiError.error || '');
  }
}

/**
 * Detect specific unauthorized error type.
 */
export function detectUnauthorizedErrorCode(apiError: ApiErrorResponse): AuthErrorCode {
  const message = (apiError.message || apiError.error || '').toLowerCase();

  if (message.includes('locked')) {
    return AuthErrorCode.ACCOUNT_LOCKED;
  }
  if (message.includes('disabled')) {
    return AuthErrorCode.ACCOUNT_DISABLED;
  }
  if (message.includes('expired')) {
    return AuthErrorCode.TOKEN_EXPIRED;
  }
  if (message.includes('mfa') || message.includes('2fa') || message.includes('two-factor')) {
    return AuthErrorCode.MFA_REQUIRED;
  }
  if (message.includes('not verified') || message.includes('unverified')) {
    return AuthErrorCode.ACCOUNT_NOT_VERIFIED;
  }

  return AuthErrorCode.INVALID_CREDENTIALS;
}

/**
 * Map API error code string to AuthErrorCode.
 */
export function mapApiCodeToAuthErrorCode(code: string): AuthErrorCode {
  const normalizedCode = code.toUpperCase().replace(/-/g, '_');

  // Direct mapping
  if (normalizedCode in AuthErrorCode) {
    return normalizedCode as AuthErrorCode;
  }

  // Common variations
  const codeMap: Record<string, AuthErrorCode> = {
    'AUTH_FAILED': AuthErrorCode.INVALID_CREDENTIALS,
    'LOGIN_FAILED': AuthErrorCode.INVALID_CREDENTIALS,
    'WRONG_PASSWORD': AuthErrorCode.INVALID_PASSWORD,
    'USER_LOCKED': AuthErrorCode.ACCOUNT_LOCKED,
    'USER_DISABLED': AuthErrorCode.ACCOUNT_DISABLED,
    'EMAIL_EXISTS': AuthErrorCode.EMAIL_ALREADY_EXISTS,
    'USER_EXISTS': AuthErrorCode.EMAIL_ALREADY_EXISTS,
    'MFA_CODE_INVALID': AuthErrorCode.MFA_INVALID_CODE,
    'OTP_INVALID': AuthErrorCode.MFA_INVALID_CODE,
    'TOKEN_EXPIRED': AuthErrorCode.TOKEN_EXPIRED,
    'RATE_LIMITED': AuthErrorCode.RATE_LIMIT_EXCEEDED,
    'TOO_MANY_REQUESTS': AuthErrorCode.RATE_LIMIT_EXCEEDED,
    'PASSWORD_WEAK': AuthErrorCode.WEAK_PASSWORD,
    'NETWORK_FAILURE': AuthErrorCode.NETWORK_ERROR,
    'CONNECTION_ERROR': AuthErrorCode.NETWORK_ERROR
  };

  return codeMap[normalizedCode] || AuthErrorCode.UNKNOWN;
}

/**
 * Detect error code from error message content.
 * @param message Error message to analyze (handles null/undefined safely)
 */
export function detectErrorCodeFromMessage(message: string | undefined | null): AuthErrorCode {
  if (!message || typeof message !== 'string') {
    return AuthErrorCode.UNKNOWN;
  }

  const lowerMessage = message.toLowerCase();

  // Credential errors
  if (lowerMessage.includes('invalid credentials') || lowerMessage.includes('wrong password') ||
      lowerMessage.includes('unauthorized')) {
    return AuthErrorCode.INVALID_CREDENTIALS;
  }

  // Account state
  if (lowerMessage.includes('locked')) {
    return AuthErrorCode.ACCOUNT_LOCKED;
  }
  if (lowerMessage.includes('disabled')) {
    return AuthErrorCode.ACCOUNT_DISABLED;
  }
  if (lowerMessage.includes('not found') || lowerMessage.includes('does not exist')) {
    return AuthErrorCode.ACCOUNT_NOT_FOUND;
  }
  if (lowerMessage.includes('already exists') || lowerMessage.includes('already registered') ||
      lowerMessage.includes('already taken')) {
    if (lowerMessage.includes('email')) {
      return AuthErrorCode.EMAIL_ALREADY_EXISTS;
    }
    if (lowerMessage.includes('username')) {
      return AuthErrorCode.USERNAME_ALREADY_EXISTS;
    }
    return AuthErrorCode.EMAIL_ALREADY_EXISTS;
  }
  if (lowerMessage.includes('not verified') || lowerMessage.includes('unverified')) {
    return AuthErrorCode.ACCOUNT_NOT_VERIFIED;
  }

  // MFA
  if (lowerMessage.includes('mfa required') || lowerMessage.includes('2fa required')) {
    return AuthErrorCode.MFA_REQUIRED;
  }
  if (lowerMessage.includes('invalid code') || lowerMessage.includes('wrong code') ||
      lowerMessage.includes('invalid verification code')) {
    return AuthErrorCode.MFA_INVALID_CODE;
  }
  if (lowerMessage.includes('code expired')) {
    return AuthErrorCode.MFA_CODE_EXPIRED;
  }

  // Rate limiting - separate too many attempts (login security) from rate limit (API throttling)
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many requests')) {
    return AuthErrorCode.RATE_LIMIT_EXCEEDED;
  }
  if (lowerMessage.includes('too many attempts')) {
    return AuthErrorCode.TOO_MANY_ATTEMPTS;
  }

  // Password reset - check BEFORE generic token/session to catch 'reset token expired' specifically
  if (lowerMessage.includes('reset') && lowerMessage.includes('expired')) {
    return AuthErrorCode.RESET_TOKEN_EXPIRED;
  }

  // Token/Session - generic checks after specific reset token check
  if (lowerMessage.includes('token expired') || lowerMessage.includes('session expired')) {
    return AuthErrorCode.TOKEN_EXPIRED;
  }
  if (lowerMessage.includes('invalid token')) {
    return AuthErrorCode.TOKEN_INVALID;
  }
  if (lowerMessage.includes('weak password') || lowerMessage.includes('password too weak')) {
    return AuthErrorCode.WEAK_PASSWORD;
  }
  if (lowerMessage.includes('recently used')) {
    return AuthErrorCode.PASSWORD_RECENTLY_USED;
  }

  // Network/Server
  if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
    return AuthErrorCode.NETWORK_ERROR;
  }
  if (lowerMessage.includes('server error') || lowerMessage.includes('internal error')) {
    return AuthErrorCode.SERVER_ERROR;
  }
  if (lowerMessage.includes('timeout')) {
    return AuthErrorCode.TIMEOUT;
  }

  // Validation
  if (lowerMessage.includes('invalid email')) {
    return AuthErrorCode.INVALID_EMAIL;
  }
  if (lowerMessage.includes('validation')) {
    return AuthErrorCode.VALIDATION_ERROR;
  }

  return AuthErrorCode.UNKNOWN;
}

/**
 * Parse a JavaScript Error object.
 *
 * @param error The Error to classify
 * @param timestamp The time the error was observed (passed in for purity)
 * @param supportUrl Optional support link copied into the result
 */
export function parseJsError(error: Error, timestamp: Date, supportUrl?: string): AuthError {
  const code = detectErrorCodeFromMessage(error.message);

  return {
    code,
    message: AUTH_ERROR_MESSAGES[code],
    technicalMessage: error.message,
    severity: AUTH_ERROR_SEVERITIES[code],
    category: AUTH_ERROR_CATEGORIES[code],
    recoveryAction: AUTH_ERROR_RECOVERY_ACTIONS[code],
    recoveryGuidance: AUTH_ERROR_RECOVERY_GUIDANCE[code],
    supportLink: supportUrl,
    originalError: error,
    timestamp,
    shouldLog: true,
    showNotification: shouldShowNotification(code)
  };
}

/**
 * Parse a string error message.
 *
 * @param error The string to classify
 * @param timestamp The time the error was observed (passed in for purity)
 * @param supportUrl Optional support link copied into the result
 */
export function parseStringError(error: string, timestamp: Date, supportUrl?: string): AuthError {
  const code = detectErrorCodeFromMessage(error);

  return {
    code,
    message: AUTH_ERROR_MESSAGES[code],
    technicalMessage: error,
    severity: AUTH_ERROR_SEVERITIES[code],
    category: AUTH_ERROR_CATEGORIES[code],
    recoveryAction: AUTH_ERROR_RECOVERY_ACTIONS[code],
    recoveryGuidance: AUTH_ERROR_RECOVERY_GUIDANCE[code],
    supportLink: supportUrl,
    originalError: error,
    timestamp,
    shouldLog: true,
    showNotification: shouldShowNotification(code)
  };
}

/**
 * Parse a generic object error (object with error-like properties).
 *
 * @param error The object to classify
 * @param timestamp The time the error was observed (passed in for purity)
 * @param supportUrl Optional support link copied into the result
 */
export function parseObjectError(
  error: Record<string, unknown>,
  timestamp: Date,
  supportUrl?: string
): AuthError {
  const message = (error['message'] as string) || (error['error'] as string) || '';
  const code = detectErrorCodeFromMessage(message);

  return {
    code,
    message: AUTH_ERROR_MESSAGES[code],
    technicalMessage: message,
    statusCode: error['statusCode'] as number || error['status'] as number,
    severity: AUTH_ERROR_SEVERITIES[code],
    category: AUTH_ERROR_CATEGORIES[code],
    recoveryAction: AUTH_ERROR_RECOVERY_ACTIONS[code],
    recoveryGuidance: AUTH_ERROR_RECOVERY_GUIDANCE[code],
    supportLink: supportUrl,
    originalError: error,
    timestamp,
    shouldLog: true,
    showNotification: shouldShowNotification(code)
  };
}

/**
 * Determine if a notification should be shown for an error code.
 * Some codes are handled by dedicated flows and stay silent.
 */
export function shouldShowNotification(code: AuthErrorCode): boolean {
  // Don't show notification for these - they're handled specially
  const silentCodes: AuthErrorCode[] = [
    AuthErrorCode.MFA_REQUIRED, // Handled by MFA flow
    AuthErrorCode.TOKEN_EXPIRED, // Handled by refresh flow
    AuthErrorCode.SESSION_EXPIRED // Handled by logout flow
  ];

  return !silentCodes.includes(code);
}

/**
 * Check if the URL is for an auth-related endpoint.
 * Only auth endpoints should map 404 to ACCOUNT_NOT_FOUND.
 */
export function isAuthRelatedEndpoint(url: string | null): boolean {
  if (!url) {
    return false;
  }

  // Auth-related endpoint patterns where 404 means account/user not found
  const authPatterns = [
    '/users/',
    '/auth/',
    '/login',
    '/register',
    '/password',
    '/mfa/',
    '/session',
    '/token',
    '/oauth/',
    '/account'
  ];

  const lowerUrl = url.toLowerCase();
  return authPatterns.some(pattern => lowerUrl.includes(pattern));
}

/**
 * Extract request ID from HTTP error headers.
 */
export function extractRequestId(error: HttpErrorResponse): string | undefined {
  // Check headers
  const requestId = error.headers?.get('x-request-id') ||
                    error.headers?.get('x-correlation-id');
  if (requestId) {
    return requestId;
  }

  return undefined;
}

/**
 * Sanitize an error object for safe logging (prevents sensitive data leakage).
 * Strips passwords, tokens, API keys, and other sensitive information.
 *
 * SECURITY-CRITICAL: byte-identical redaction logic preserved from the original
 * service. The HttpErrorResponse branch deliberately excludes the response body
 * and headers; the generic-object branch redacts sensitive field NAMES outright
 * and scrubs sensitive params out of string VALUES.
 */
export function sanitizeErrorForLogging(error: unknown): Record<string, unknown> {
  // Sensitive field patterns to redact
  const sensitivePatterns = /password|token|secret|key|credential|auth|cookie|session|bearer/i;
  // Text-mode scrubber for `error.message` (which is a free-form string that
  // may contain a URL substring among other text). Broadened from the
  // original token|password|code|... set to match more credential conventions.
  // For the URL field itself we use the canonical sanitizeUrl utility, which
  // also handles fragment params and the full 51-key sensitive set.
  const urlSensitiveParams = /([?&])(access_token|api_key|apikey|auth|authorization|bearer|code|csrf_token|csrf|id_token|jwt|key|magic_link|magic|mfa|nonce|otp|password|passwd|pwd|refresh_token|reset_token|reset|saml|secret|session_id|sessionid|session|sid|signature|sig|state|ticket|token_type|token|totp|verification|verify|xsrf_token|xsrf)=[^&#]*/gi;

  if (error instanceof HttpErrorResponse) {
    return {
      status: error.status,
      statusText: error.statusText,
      url: error.url ? sanitizeUrl(error.url) : undefined,
      // Sanitize message too - it may contain a URL substring with sensitive params.
      message: error.message?.replace(urlSensitiveParams, '$1$2=***'),
      errorType: 'HttpErrorResponse'
      // Deliberately exclude: error.error (response body), error.headers
    };
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      errorType: error.constructor.name
      // Deliberately exclude: stack trace (may contain sensitive context)
    };
  }

  if (typeof error === 'object' && error !== null) {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(error)) {
      // Skip sensitive fields entirely
      if (sensitivePatterns.test(key)) {
        sanitized[key] = '***REDACTED***';
      } else if (typeof value === 'string') {
        // Redact sensitive values in URLs
        sanitized[key] = value.replace(urlSensitiveParams, '$1$2=***');
      } else if (typeof value === 'object' && value !== null) {
        // Don't recurse into nested objects - just note they exist
        sanitized[key] = '[object]';
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }

  return { value: '[sanitized]' };
}
